import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import i18n from '@/i18n'
import { preparePlatformAccess, isPlatformAccessTokenValid } from '@/utils/accessGate'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      titleKey: 'bottomNav.首页',
      keepAlive: true
    }
  },
  {
    path: '/investment',
    name: 'Investment',
    redirect: '/',
    meta: {
      titleKey: 'bottomNav.投资',
      hidden: true
    }
  },
  {
    path: '/wealth',
    name: 'WealthMarket',
    component: () => import('../views/WealthMarket.vue'),
    meta: {
      titleKey: 'bottomNav.算力申请',
      keepAlive: true
    }
  },
  {
    path: '/wealth/product/:code',
    name: 'WealthProduct',
    component: () => import('../views/WealthProduct.vue'),
    meta: {
      titleKey: 'wealth.理财产品',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/wealth/positions',
    name: 'WealthPositions',
    component: () => import('../views/WealthPositions.vue'),
    meta: {
      titleKey: 'wealth.我的持仓'
    }
  },
  {
    path: '/wealth/position/:id',
    name: 'WealthPositionDetail',
    component: () => import('../views/WealthPositionDetail.vue'),
    meta: {
      titleKey: 'wealth.持仓详情',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/farm',
    name: 'Farm',
    component: () => import('../views/Farm.vue'),
    meta: {
      titleKey: 'bottomNav.农场',
      keepAlive: true
    }
  },
  {
    path: '/computing',
    redirect: '/wealth'
  },
  {
    path: '/translation-test',
    name: 'TranslationTest',
    component: () => import('../views/TranslationTest.vue'),
    meta: {
      titleKey: 'translationTest.pageTitle'
    }
  },
  {
    path: '/computing/applications',
    redirect: '/wealth/positions'
  },
  {
    path: '/computing/unstake',
    redirect: '/wealth/positions'
  },
  {
    path: '/computing/application/:id',
    redirect: '/wealth/positions'
  },
  {
    path: '/assets',
    name: 'Assets',
    component: () => import('../views/Assets.vue'),
    meta: {
      titleKey: 'bottomNav.资产',
      keepAlive: true
    }
  },
  {
    path: '/deposit',
    name: 'Deposit',
    component: () => import('../views/Deposit.vue'),
    meta: {
      titleKey: 'recharge.充值',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/manual-deposit',
    name: 'ManualDeposit',
    component: () => import('../views/ManualDeposit.vue'),
    meta: {
      titleKey: 'recharge.充值',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/flash-swap',
    name: 'FlashSwap',
    component: () => import('../views/FlashSwap.vue'),
    meta: {
      titleKey: 'assets.闪兑',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/recharge',
    name: 'Recharge',
    component: () => import('../views/Recharge.vue'),
    meta: {
      titleKey: 'recharge.充值',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/recharge-history',
    name: 'RechargeHistory',
    component: () => import('../views/RechargeHistory.vue'),
    meta: {
      titleKey: 'recharge.充值记录',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/recharge-detail/:orderNumber',
    name: 'RechargeDetail',
    component: () => import('../views/RechargeDetail.vue'),
    meta: {
      titleKey: 'rechargeDetail.充值详情',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/airdrop-rewards',
    name: 'AirdropRewards',
    component: () => import('../views/AirdropRewards.vue'),
    meta: {
      titleKey: 'airdropRewards.空投奖励'
    }
  },
  {
    path: '/balance-logs',
    name: 'BalanceLogs',
    component: () => import('../views/BalanceLogs.vue'),
    meta: {
      titleKey: 'balanceLogs.账变记录',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue'),
    meta: {
      titleKey: 'about.pageTitle'
    }
  },
  {
    path: '/whitepaper',
    name: 'Whitepaper',
    component: () => import('../views/Whitepaper.vue'),
    meta: {
      titleKey: 'whitepaper.白皮书',
      hideBottomNav: true
    }
  },

  {
    path: '/robot-details/:robotId?',
    name: 'RobotDetails',
    redirect: '/',
    meta: { hidden: true }
  },
  {
    path: '/robot-investment/:id',
    name: 'RobotInvestment',
    redirect: '/',
    meta: { hidden: true }
  },
  {
    path: '/icon-test',
    name: 'IconTest',
    component: () => import('../views/IconTest.vue'),
    meta: {
      titleKey: 'iconTest.pageTitle'
    }
  },
  {
    path: '/orders',
    name: 'Orders',
    redirect: '/',
    meta: { hidden: true }
  },
  {
    path: '/earnings',
    name: 'Earnings',
    redirect: '/',
    meta: { hidden: true }
  },
  {
    path: '/withdrawal',
    name: 'Withdrawal',
    component: () => import('../views/Withdrawal.vue'),
    meta: {
      titleKey: 'withdrawal.提现',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/withdrawal-history',
    name: 'WithdrawalHistory',
    component: () => import('../views/WithdrawalHistory.vue'),
    meta: {
      titleKey: 'withdrawal.提现记录',
      hideHeader: true,
      hideBottomNav: true
    }
  },
  {
    path: '/set-fund-password',
    name: 'SetFundPassword',
    component: () => import('../views/SetFundPassword.vue'),
    meta: {
      titleKey: 'setFundPassword.设置资金密码',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/kyc',
    name: 'Kyc',
    component: () => import('../views/Kyc.vue'),
    meta: {
      titleKey: 'kyc.身份认证',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/modify-fund-password',
    name: 'ModifyFundPassword',
    component: () => import('../views/ModifyFundPassword.vue'),
    meta: {
      titleKey: 'modifyFundPassword.修改安全密码',
      hideHeader: true,
      hideBottomNav: true
    }
  },
  {
    path: '/lucky-wheel',
    redirect: '/assets'
  },
  {
    path: '/apply-funds',
    redirect: '/assets'
  },
  {
    path: '/loan-records',
    redirect: '/assets'
  },
  {
    path: '/loan-rules',
    redirect: '/assets'
  },
  {
    path: '/messages',
    name: 'Messages',
    component: () => import('../views/Messages.vue'),
    meta: {
      titleKey: 'messages.title',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/messages/:id',
    name: 'MessageDetail',
    component: () => import('../views/Messages.vue'),
    meta: {
      titleKey: 'messages.title',
      hideBottomNav: true,
      hideHeader: true
    }
  },
  {
    path: '/credit-score',
    redirect: '/assets'
  },
  {
    path: '/credit-score-legacy',
    name: 'CreditScore',
    component: () => import('../views/CreditScore.vue'),
    meta: {
      titleKey: 'creditScore.pageTitle',
      hideBottomNav: true
    }
  },
  {
    path: '/flash-options',
    name: 'FlashOptions',
    redirect: '/',
    meta: {
      hidden: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue'),
    meta: {
      titleKey: 'notFound.title'
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 页面标题统一英文（钱包/DApp 历史记录会读取 document.title，避免出现中文）
const SITE_DOCUMENT_TITLE = 'Plenty - Plenty Vertical Farming'
const hasChinese = (text) => /[\u4e00-\u9fff]/.test(String(text || ''))

const updatePageTitle = (route) => {
  let title = SITE_DOCUMENT_TITLE
  if (route.meta.titleKey) {
    try {
      const enTitle = i18n.global.t(route.meta.titleKey, {}, { locale: 'en' })
      if (enTitle && enTitle !== route.meta.titleKey && !hasChinese(enTitle)) {
        title = `${enTitle} - Plenty`
      }
    } catch (_e) { /* keep English site title */ }
  } else if (route.meta.title && !hasChinese(route.meta.title)) {
    title = route.meta.title
  }
  document.title = title
}

// 路由守卫 - 设置页面标题 + 授权码兜底（配置仅首次强制拉取，切换 Tab 走缓存）
let accessConfigReady = false
router.beforeEach(async (to, from, next) => {
  updatePageTitle(to)
  try {
    const access = await preparePlatformAccess({ forceConfig: !accessConfigReady })
    accessConfigReady = true
    if (access.granted || !access.config?.enabled) {
      next()
      return
    }
    if (!isPlatformAccessTokenValid()) {
      window.location.reload()
      next(false)
      return
    }
    next()
  } catch {
    next()
  }
})

// 监听语言切换，更新当前页面标题
let currentRoute = null
router.afterEach((to) => {
  currentRoute = to
})

// 导出更新标题函数，供语言切换时调用
window.updateCurrentPageTitle = () => {
  if (currentRoute) {
    updatePageTitle(currentRoute)
  }
}

export default router
